import React, { ChangeEvent, useCallback, useEffect, useState } from 'react';
import { batch, useDispatch, useSelector } from 'react-redux';

import { Modal } from '@vectord/components';
import {
  AddMenteeModuleModalStateSelector,
  setAddMenteeModule,
  selectedMenteeModuleSelector,
  setMenteeModuleError,
  setAddMenteeModuleModalState,
  AddMenteeModuleSelector,
  menteeModuleErrorSelector,
  setMenteeModuleType,
} from 'features/moduleManagement';
import AddMenteeModuleConfirmButton from '../AddMenteeModuleConfirmButton';
import styles from './AddMenteeModuleModal.module.scss';
import AddMenteeModuleModalField from '../AddMenteeModuleModalField/AddMenteeModuleModalField';
import FieldErrors from 'components/FieldErrors';
import SuperBatchModuleInput from '../SuperBatchModuleInput';

function AddMenteeModuleModal({ user, superBatch }: IAddMenteeModuleModal) {
  const menteeModuleToBeAdded = useSelector(AddMenteeModuleSelector);
  const dispatch = useDispatch();
  const selectedMenteeModule = useSelector(selectedMenteeModuleSelector);
  const isModalOpen = useSelector(AddMenteeModuleModalStateSelector);
  const { email } = user || {};
  const fieldErrors = useSelector(menteeModuleErrorSelector);

  const [interviewRequired, setInterviewRequired] = useState(false);
  const [moduleType, setModuleType] = useState<IAddModuleTypeoption | null>(
    null
  );
  const [skillContest, setSkillContest] = useState(false);

  const handleInterviewRequired = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      dispatch(setMenteeModuleError(null));
      setInterviewRequired(event.target.checked);
    },
    [dispatch]
  );

  const handleModuleTypeChange = useCallback(
    (option: IAddModuleTypeoption) => {
      dispatch(setMenteeModuleError(null));
      dispatch(setMenteeModuleType(option));
      setModuleType(option);
    },
    [dispatch]
  );

  const handleSkillContestChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      dispatch(setMenteeModuleError(null));
      setSkillContest(event.target.checked);
    },
    [dispatch]
  );

  const handleAddMenteeModuleChange = useCallback(
    (module: IModule) => {
      dispatch(
        setAddMenteeModule({
          ...module,
          email: email,
          super_batch_name: superBatch?.name,
          before_module_id: selectedMenteeModule?.academy_module?.id,
          interview_required: interviewRequired,
          skill_contest_required: skillContest,
        })
      );
    },
    [
      dispatch,
      email,
      superBatch,
      selectedMenteeModule,
      interviewRequired,
      skillContest,
    ]
  );

  useEffect(() => {
    dispatch(
      setAddMenteeModule({
        ...menteeModuleToBeAdded,
        interview_required: interviewRequired,
        skill_contest_required: skillContest,
        before_module_id: selectedMenteeModule?.academy_module?.id,
      })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [interviewRequired, skillContest, selectedMenteeModule]);

  useEffect(() => {
    dispatch(
      setAddMenteeModule({
        ...menteeModuleToBeAdded,
        academy_module_id: null,
        academy_module_name: null,
      })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, moduleType]);

  const handleOnClose = useCallback(() => {
    batch(() => {
      dispatch(setAddMenteeModule(null));
      dispatch(setAddMenteeModuleModalState(false));
      dispatch(setMenteeModuleType(null));
      setInterviewRequired(false);
      setModuleType(null);
      setSkillContest(false);
    });
  }, [dispatch]);
  return (
    <Modal
      isOpen={isModalOpen}
      unMountOnClose
      title="Add Mentee Module"
      onClose={handleOnClose}
      className="p-xs"
      size="large"
      canClose
    >
      <div className={styles.modalInput}>
        <div className="row m-t-10 m-b-20 align-c">
          <span className="h4 dark bold">Batch: </span>
          <span className="h4 m-l-10">{superBatch?.name}</span>
        </div>
        <AddMenteeModuleModalField
          selectedMenteeModule={selectedMenteeModule}
          interviewRequired={interviewRequired}
          handleInterviewRequired={handleInterviewRequired}
          moduleType={moduleType}
          handleModuleTypeChange={handleModuleTypeChange}
          skillContest={skillContest}
          handleSkillContestChange={handleSkillContestChange}
        />
        <SuperBatchModuleInput
          superBatch={superBatch}
          onChange={handleAddMenteeModuleChange as (module: IModule) => void}
        />
        <FieldErrors
          className="m-t-10"
          name={'menteeModuleErrors'}
          errors={fieldErrors}
        />
      </div>
      <AddMenteeModuleConfirmButton />
    </Modal>
  );
}

export default AddMenteeModuleModal;
